import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ChevronDown as ChevronDownIcon, ChevronRight as ChevronRightIcon } from "react-feather";

import { ADMIN_EVENT_TIMEZONE_SUPPLY } from "../../gql/queries/eventInsights";
import { ZONE_ORDER } from "../../helpers/const";
import type { ZoneLabel } from "../../types/graphql";
import { zoneHex } from "./helpers";
import type { EventTimezoneSupply, TzRegion } from "./types";

/**
 * The bridge between the UTC colour zones and real time-of-day.
 *
 * A colour zone is a fixed 3-hour UTC slot — not a "time of day". For a global
 * community that slot is a different local time everywhere: 09–12 UTC is
 * mid-morning in Europe, the small hours in the Americas and evening in
 * Australia. This shows EVERY member region (rows) against all eight zones
 * (columns), so "GREEN" reads as "morning for whom?" for the whole community —
 * no region left out. Live UTC offsets, so it stays right through daylight
 * saving.
 */

/** [startHour, endHour) in UTC for a colour zone (bands are 3 hours, in order). */
const zoneUtcRange = (zone: ZoneLabel): [number, number] => {
  const i = ZONE_ORDER.indexOf(zone);
  const start = i * 3;
  return [start, start + 3];
};

const pad = (n: number): string => String(n).padStart(2, "0");
const fmtMin = (min: number): string => {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${pad(hh)}:${pad(mm)}` : `${pad(hh)}`;
};

const dayPart = (localStartMin: number): "morning" | "afternoon" | "evening" | "night" => {
  const hr = Math.floor((((localStartMin % 1440) + 1440) % 1440) / 60);
  if (hr >= 6 && hr <= 11) {
    return "morning";
  }
  if (hr >= 12 && hr <= 17) {
    return "afternoon";
  }
  if (hr >= 18 && hr <= 22) {
    return "evening";
  }
  return "night";
};

/** Subtle tint so civilised hours (evening) and antisocial ones (night) stand out. */
const PART_TINT: Record<string, string> = {
  morning: "transparent",
  afternoon: "transparent",
  evening: "rgba(0, 88, 61, 0.12)",
  night: "rgba(163, 45, 45, 0.10)",
};

const utcOffsetLabel = (hours: number): string => {
  const sign = hours >= 0 ? "+" : "−";
  const total = Math.round(Math.abs(hours) * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `UTC${sign}${hh}${mm ? `:${pad(mm)}` : ""}`;
};

const ZoneDot: React.FC<{ zone: ZoneLabel }> = ({ zone }) => (
  <Box
    component="span"
    sx={{
      width: 9,
      height: 9,
      borderRadius: "50%",
      backgroundColor: zoneHex(zone),
      display: "inline-block",
    }}
  />
);

const ZoneClockReference: React.FC = () => {
  const [open, setOpen] = useState(true);
  const { data } = useQuery(ADMIN_EVENT_TIMEZONE_SUPPLY, {
    variables: { days: 7 },
    fetchPolicy: "cache-first",
  });
  const supply: EventTimezoneSupply | undefined = data?.adminEventTimezoneSupply;

  const regions: TzRegion[] = supply?.regions ?? [];
  if (regions.length === 0) {
    return null;
  }

  // Run the rows west → east, from the most-minus UTC offset to the most-plus —
  // one continuous progression, which keeps same regions naturally adjacent.
  const ordered = [...regions].sort((a, b) => a.offset - b.offset);

  return (
    <Box mb={2}>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        startIcon={open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        sx={{ textTransform: "none", color: "text.secondary", px: 0.5 }}
      >
        Which local time is each zone, for every region? (a zone is a fixed 3‑hour UTC slot)
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            overflowX: "auto",
            mt: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Table size="small" sx={{ minWidth: 820, "& td, & th": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Region ╲ Zone</TableCell>
                {ZONE_ORDER.map((zone) => {
                  const [uStart, uEnd] = zoneUtcRange(zone);
                  return (
                    <TableCell key={zone} align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          fontWeight: 600,
                        }}
                      >
                        <ZoneDot zone={zone} />
                        {zone}
                      </Box>
                      <Box sx={{ fontSize: 10, color: "text.secondary" }}>
                        {pad(uStart)}–{pad(uEnd)} UTC
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {ordered.map((r) => (
                <TableRow key={`${r.offset}-${r.region}`} hover>
                  <TableCell>
                    <Box sx={{ fontWeight: 600 }}>{r.region}</Box>
                    <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                      {utcOffsetLabel(r.offset)} · {r.members.toLocaleString("en-GB")} members
                    </Box>
                  </TableCell>
                  {ZONE_ORDER.map((zone) => {
                    const [uStart] = zoneUtcRange(zone);
                    const localStart = uStart * 60 + r.offset * 60;
                    const part = dayPart(localStart);
                    return (
                      <TableCell
                        key={zone}
                        align="center"
                        sx={{
                          backgroundColor: PART_TINT[part],
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        <Box>
                          {fmtMin(localStart)}–{fmtMin(localStart + 180)}
                        </Box>
                        <Box sx={{ fontSize: 10, color: "text.secondary" }}>{part}</Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Typography variant="caption" color="textSecondary" component="p" mt={1}>
          Every member region is here (rows), against all eight zones (columns). A colour zone is a
          fixed <b>3‑hour UTC window</b> — the unit you actually schedule an event in, since an
          event happens at one UTC time for the whole world. What <b>local</b> time that is depends
          on where a member lives, so the same zone is morning for some and the middle of the night
          for others. Evening (a civilised hour) is tinted green; the small hours are tinted red.
        </Typography>
      </Collapse>
    </Box>
  );
};

export default ZoneClockReference;
